import { useQuery } from "@tanstack/react-query";
import { fetchBuildSteps } from "~/components/build/Show/lib/queries";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { Build } from "~/components/build/Show/lib/types";

export const stepsByStateQueryKey = (build: Build, state: string) => {
  return ["build", build.id, "steps", `filter:${state}`];
};

export const allStepsQueryKey = (build: Build) => {
  return ["build", build.id, "steps", "all"];
};

export const topLevelStepsQueryKey = (build: Build) => {
  return ["build", build.id, "steps", "depth:top"];
};

/**
 * Fetch **all** the steps for a build.
 */
export function useStepsQuery(build: Build, options?: { enabled: boolean }) {
  if (!Features.ProgressiveBuildShow) {
    return {
      isLoading: false,
      refetch: () => Promise.resolve(),
    };
  }

  return useQuery({
    queryKey: allStepsQueryKey(build),
    queryFn: () => fetchBuildSteps({ buildDataBasePath: build.buildDataBasePath }),
    ...options,
  });
}

/**
 * Fetch (ungrouped) steps for a build filtered by state.
 */
export const useStepsForStateQuery = (
  build: Build,
  state: string,
  options?: { enabled: boolean },
) => {
  // With feature disabled we've got all the data already so resolve promise.
  if (!Features.ProgressiveBuildShow) {
    return {
      isLoading: false,
      data: null,
    };
  }

  return useQuery({
    queryKey: stepsByStateQueryKey(build, state),
    queryFn: () =>
      fetchBuildSteps({
        buildDataBasePath: build.buildDataBasePath,
        filters: { state, excludeGroupSteps: true },
      }),
    ...options,
  });
};

/**
 * Fetch **top-level** steps for a build (i.e group steps and ungrouped steps)
 */
export function useTopLevelStepsQuery(build: Build) {
  if (!Features.ProgressiveBuildShow) {
    return {
      isLoading: false,
      steps: [],
    };
  }

  return useQuery({
    queryKey: topLevelStepsQueryKey(build),
    queryFn: () =>
      fetchBuildSteps({
        buildDataBasePath: build.buildDataBasePath,
        filters: { depth: "top" },
      }),
  });
}

/**
 * Get steps from the build store.
 *
 * The store is updated by the BuildContext.
 */
export function useSteps() {
  const steps = useBuildStore((state) => state.steps);
  const stepsMap = useBuildStore((state) => state.stepsMap);
  const groupStepsMap = useBuildStore((state) => state.stepsGroupedByParent);
  const stepJobs = useBuildStore((state) => state.stepJobs);

  return {
    steps,
    stepsMap,
    groupStepsMap,
    stepJobs,
  };
}
